import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import { useAuth } from '../../contexts/AuthContext';

const USER_ROLES = {
  consumer: { label: 'Consumer', color: 'text-accent', bg: 'bg-[rgba(6,182,212,0.12)]' },
  retail: { label: 'Retail Partner', color: 'text-[#10B981]', bg: 'bg-[rgba(16,185,129,0.12)]' },
  financial: { label: 'Financial Partner', color: 'text-[#F59E0B]', bg: 'bg-[rgba(245,158,11,0.12)]' },
  admin: { label: 'System Admin', color: 'text-[#EF4444]', bg: 'bg-[rgba(239,68,68,0.12)]' },
};

const MENU_ITEMS = [
  { id: 'profile', label: 'My Profile', icon: 'User', path: '/home-dashboard' },
  { id: 'settings', label: 'Settings', icon: 'Settings', path: '/onboarding-dashboard' },
  { id: 'notifications', label: 'Notifications', icon: 'Bell', path: '/home-dashboard' },
  { id: 'help', label: 'Help & Support', icon: 'HelpCircle', path: '/home-dashboard' },
  { id: 'divider' },
  { id: 'signout', label: 'Sign Out', icon: 'LogOut', path: '/', danger: true },
];

export default function UserContextMenu({
  user = {
    name: 'Alex Morgan',
    email: 'alex.morgan@email.com',
    role: 'consumer',
    avatar: '',
  },
}) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const roleInfo = USER_ROLES?.[user?.role] || USER_ROLES?.consumer;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef?.current && !menuRef?.current?.contains(e?.target)) {
        setIsOpen(false);
      }
    };
    const handleEsc = (e) => {
      if (e?.key === 'Escape') {
        setIsOpen(false);
        triggerRef?.current?.focus();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEsc);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen]);

  const handleToggle = () => setIsOpen((prev) => !prev);

  const handleMenuItemClick = async (item) => {
    if (item?.id === 'divider') return;
    setIsOpen(false);
    if (item?.id === 'signout') {
      await signOut();
      navigate('/onboarding-dashboard');
      return;
    }
    navigate(item?.path);
  };

  const initials = user?.name?.split(' ')?.map((n) => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2);

  return (
    <div ref={menuRef} className="relative flex items-center">
      <button
        ref={triggerRef}
        onClick={handleToggle}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label={`User menu for ${user?.name}`}
        className="flex items-center gap-2.5 px-3 py-2 rounded-lg border border-[rgba(6,182,212,0.1)] bg-card hover:border-[rgba(6,182,212,0.25)] hover:bg-[rgba(6,182,212,0.06)] transition-all duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-primary min-h-[44px] active:scale-[0.97]"
      >
        {/* Avatar */}
        <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0 bg-[rgba(6,182,212,0.15)] flex items-center justify-center">
          {user?.avatar ? (
            <Image
              src={user?.avatar}
              alt={`${user?.name} avatar`}
              className="w-full h-full object-cover"
            />
          ) : (
            <span className="font-heading font-semibold text-xs text-accent">{initials}</span>
          )}
        </div>

        {/* Name & Role - hidden on small screens */}
        <div className="hidden lg:flex flex-col items-start min-w-0">
          <span className="font-heading font-semibold text-sm text-[#F8FAFC] truncate max-w-[120px]">
            {user?.name}
          </span>
          <span className={`font-caption text-[10px] font-medium ${roleInfo?.color}`}>
            {roleInfo?.label}
          </span>
        </div>

        <Icon
          name="ChevronDown"
          size={16}
          color="var(--color-muted-foreground)"
          className={`transition-transform duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {/* Dropdown */}
      {isOpen && (
        <div
          role="menu"
          aria-label="User account menu"
          className="absolute top-full right-0 mt-2 w-64 bg-card border border-[rgba(6,182,212,0.15)] rounded-lg shadow-glow-lg z-[200] animate-scale-in overflow-hidden"
        >
          {/* User Info Header */}
          <div className="px-4 py-4 border-b border-[rgba(6,182,212,0.1)]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0 bg-[rgba(6,182,212,0.15)] flex items-center justify-center">
                {user?.avatar ? (
                  <Image
                    src={user?.avatar}
                    alt={`${user?.name} avatar`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="font-heading font-semibold text-sm text-accent">{initials}</span>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-heading font-semibold text-sm text-[#F8FAFC] truncate">
                  {user?.name}
                </p>
                <p className="font-caption text-xs text-[#94A3B8] truncate">{user?.email}</p>
                <span
                  className={`inline-block mt-1 px-2 py-0.5 rounded-full text-[10px] font-medium font-caption ${roleInfo?.bg} ${roleInfo?.color}`}
                >
                  {roleInfo?.label}
                </span>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {MENU_ITEMS?.map((item, idx) => {
              if (item?.id === 'divider') {
                return (
                  <div
                    key={`divider-${idx}`}
                    className="my-1 border-t border-[rgba(6,182,212,0.08)]"
                    role="separator"
                  />
                );
              }
              return (
                <button
                  key={item?.id}
                  role="menuitem"
                  onClick={() => handleMenuItemClick(item)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-all duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring min-h-[44px] ${
                    item?.danger
                      ? 'text-[#EF4444] hover:bg-[rgba(239,68,68,0.08)]'
                      : 'text-[#CBD5E1] hover:text-[#F8FAFC] hover:bg-[rgba(6,182,212,0.06)]'
                  }`}
                >
                  <Icon
                    name={item?.icon}
                    size={16}
                    color={item?.danger ? '#EF4444' : 'currentColor'}
                    strokeWidth={2}
                  />
                  <span className="font-body">{item?.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}